import React, { useState, useEffect, useRef } from 'react';
import './CompteARebourd.css';

interface CompteARebourdProps {
  targetDate: string; // Format attendu : "2025-10-17 13:07:55"
  initialTime?: string; // Optionnel
  onExpire?: () => void; // Callback quand le chrono atteint 0
  autoReload?: boolean; // Recharger automatiquement la page quand le chrono atteint 0 (défaut: true)
  source?: string; // BC, MS, ou BCI - Les BCI ne doivent pas afficher de compte à rebours
}

const CompteARebourd: React.FC<CompteARebourdProps> = ({
  targetDate,
  initialTime,
  onExpire,
  autoReload = true,
  source
}) => {
  // CORRECTION: Ne rien afficher pour les BCI (Paniers importés)
  if (source === 'BCI') {
    return null;
  }
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    if (targetDate) return calculateTimeLeft(targetDate);
    if (initialTime) return parseTimeString(initialTime);
    return 0;
  });

  const [isExpiring, setIsExpiring] = useState(false);
  const hasExpiredRef = useRef(false);
  const reloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      if (targetDate) {
        setTimeLeft(calculateTimeLeft(targetDate));
      } else {
        setTimeLeft(prev => (prev > 0 ? prev - 1 : 0));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  // NOUVEAU: Effet pour gérer l'expiration
  useEffect(() => {
    if (timeLeft === 0 && !hasExpiredRef.current) {
      hasExpiredRef.current = true;
      setIsExpiring(true);
      console.log('⏰ Chronomètre expiré - Rechargement dans 2 secondes...');

      // NOUVEAU: Déclencher un événement personnalisé pour mettre à jour le statut
      const event = new CustomEvent('chronoExpired', {
        detail: { expired: true },
        bubbles: true
      });
      window.dispatchEvent(event);

      // Appeler le callback si fourni
      if (onExpire) {
        onExpire();
      }

      // Recharger la page après 2 secondes si autoReload est activé
      if (autoReload) {
        reloadTimeoutRef.current = setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    }

    return () => {
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
      }
    };
  }, [timeLeft, onExpire, autoReload]);

  const formatTime = (seconds: number): string => {
    const days = Math.floor(seconds / (24 * 3600));
    const hours = Math.floor((seconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${days}j ${hours}h ${minutes}m ${secs}s`;
  };

  const getUrgencyLevel = (seconds: number): string => {
    const days = Math.floor(seconds / (24 * 3600));
    if (seconds <= 0) return 'expired';
    if (days <= 2) return 'urgent';
    if (days <= 6) return 'warning';
    if (days <= 90) return 'normal';
    return '';
  };

  return (
    <div className={`countdown ${getUrgencyLevel(timeLeft)}`}>
      {isExpiring ? (
        <span>Expiré</span>
      ) : (
        formatTime(timeLeft)
      )}
    </div>
  );
};

// 🔹 Convertit un texte de durée ("2j 5h 10m") en secondes
const parseTimeString = (timeStr: string): number => {
  const parts = timeStr.split(' ');
  let totalSeconds = 0;

  parts.forEach(part => {
    const value = parseInt(part);
    if (part.endsWith('j')) totalSeconds += value * 24 * 3600;
    if (part.endsWith('h')) totalSeconds += value * 3600;
    if (part.endsWith('m')) totalSeconds += value * 60;
    if (part.endsWith('s')) totalSeconds += value;
  });

  return totalSeconds;
};

//  Calcule le temps restant en secondes entre maintenant et la date cible (locale)
const calculateTimeLeft = (targetDateStr: string): number => {
  // Format attendu : "YYYY-MM-DD HH:MM:SS"
  const [datePart, timePart] = targetDateStr.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);

  //  Ici, on crée une date locale (pas UTC)
  const targetDate = new Date(year, month - 1, day, hour, minute, second);
  const now = new Date();

  if (isNaN(targetDate.getTime())) {
    console.error('Format de date invalide :', targetDateStr);
    return 0;
  }

  const differenceInSeconds = Math.floor((targetDate.getTime() - now.getTime()) / 1000);
  return Math.max(0, differenceInSeconds);
};


export default CompteARebourd;
